import React from "react"
import dynamic from "next/dynamic"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import {
  footerUrl,
  headerUrl,
  publicWebsiteUrl,
  COMPONENT_TYPES,
  CORE_GLOBAL_CONFIG_URL,
  PRODUCT_GLOBALCONFIG_DATA_URL,
  PRODUCT_DATA_URL,
} from "@/constants"
import { SELECT_AFTER_LAST_SLASH_REGEX } from "@/constants/regex"
import {
  generateCanonicalUrl,
  filterBackEndJson,
  getClienIP,
  updateAnalyticPageUrl,
} from "@/utils/helper"
import { isObjectEmpty, recursiveDataFunction } from "@/utils/"
import { getBundleProductDetailsFromServer } from "@/services/productServices"
import {
  fetchGlobalConfig,
  fetchPageModel,
  fetchPageShell,
} from "@/services/server/aemPageData.server"
import {
  getBVFullReviewData,
  getBVReviewStatsData,
  getProductDetailsFromServer,
} from "@/utils/product"
import {
  getUpstreamStatus,
  handleServerSideRouteError,
  logServerSideError,
} from "@/utils/serverSideError"
import akamaiCacheTagNormalization from "@/utils/akamaiCacheTagNormalization"
const BundlePdpPage = dynamic(() => import("@/components/BundlePdpPage"))
const ProductDetails = dynamic(() => import("@/components/ProductDetailsPage"))

const BundlePdp = props => {
  const { isMultiFinishBundle = false } = props || {}
  return isMultiFinishBundle ? (
    <BundlePdpPage {...props} />
  ) : (
    <ProductDetails {...props} />
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({
  req,
  res,
  resolvedUrl,
  locale,
  query = {},
}) {
  await i18n?.reloadResources()
  const { originalRequestPath = "", skuId = "" } = query
  const url = resolvedUrl

  const profileName = `profile_kohler_PDP`
  const collections = "kohler"
  const configUrl = url.replace(
    SELECT_AFTER_LAST_SLASH_REGEX,
    PRODUCT_GLOBALCONFIG_DATA_URL
  )
  const globalConfigUrl = `${CORE_GLOBAL_CONFIG_URL}?caConfig=true&path=/${locale}${configUrl}`
  const pageModelUrl = `/${locale}${PRODUCT_DATA_URL}`
  const pageModelRequest = fetchPageModel(pageModelUrl).then(
    data => ({ data }),
    error => ({ error })
  )
  const slug = originalRequestPath.split("/").at(-1) || ""
  const errorsData = {
    pdpData: null,
    pageModelError: null,
    headerModelError: null,
    footerModelError: null,
    configDataError: null,
  }
  let pdpData = {}
  let configData = {}
  let configDataError
  try {
    configData = (await fetchGlobalConfig(globalConfigUrl)) || {}
  } catch (error) {
    configDataError = error
  }
  if (configDataError) {
    return handleServerSideRouteError(
      "Bundle PDP global config fetch failed",
      configDataError,
      {
        url: configUrl,
        slug,
      }
    )
  }
  const { general: { lwAppName = "" } = {} } = configData
  try {
    pdpData = await getBundleProductDetailsFromServer(slug, lwAppName)
  } catch (error) {
    if (getUpstreamStatus(error) === 404) {
      logServerSideError(
        "Bundle PDP product details fetch returned 404",
        error,
        {
          slug,
          lwAppName,
        }
      )
    } else {
      logServerSideError("Bundle PDP product details fetch failed", error, {
        slug,
        lwAppName,
      })
      throw error
    }
  }
  const { BundleSku_s: BundleSku } = pdpData
  const isMultiFinishBundle = Boolean(BundleSku)
  if (!isMultiFinishBundle) {
    try {
      pdpData = await getProductDetailsFromServer(
        profileName,
        collections,
        slug,
        locale
      )
    } catch (error) {
      return handleServerSideRouteError(
        "Bundle PDP fallback product details fetch failed",
        error,
        {
          profileName,
          collections,
          slug,
          locale,
        }
      )
    }
  }
  if (isObjectEmpty(pdpData) && !errorsData.pdpData) {
    const error = new Error("Bundle PDP product details returned empty data")
    logServerSideError(
      "Bundle PDP product details returned empty data",
      error,
      {
        slug,
        profileName,
        collections,
        locale,
      }
    )
    throw error
  }
  let pageModelData = {}
  const { data: resolvedPageModel, error: pageModelError } =
    await pageModelRequest
  if (pageModelError) {
    return handleServerSideRouteError(
      "Bundle PDP page model fetch failed",
      pageModelError,
      {
        url: pageModelUrl,
        slug,
      }
    )
  }
  pageModelData = resolvedPageModel || {}
  const {
    headerExpFrg = "",
    footerExpFrg = "",
    pageComponentProperties,
    data: rawAnalyticsData = "",
    seoProperties = {},
    globalScript = "",
    footerScript = "",
    datalayerPageName = "",
    eventPageType = "",
    siteName = "",
    pageType = "",
  } = pageModelData
  const headerModelError = null
  const headerModelUrl = `${headerExpFrg || headerUrl}.model.json`
  const footerModelError = null
  const footerModelUrl = `${footerExpFrg || footerUrl}.model.json`
  const { header, footer } = await fetchPageShell({
    headerModelUrl,
    footerModelUrl,
    pageName: "Bundle PDP",
    context: {
      slug,
    },
  })
  if (pageModelError) errorsData.pageModelError = pageModelError
  if (headerModelError) errorsData.headerModelError = headerModelError
  if (footerModelError) errorsData.footerModelError = footerModelError
  if (configDataError) errorsData.configDataError = configDataError
  const fullUrl = generateCanonicalUrl(req?.url, locale, publicWebsiteUrl)

  const pageModelDataTmp = pageComponentProperties || pageModelData
  const tmpData = filterBackEndJson(pageModelDataTmp)
  const filterData = filterBackEndJson(tmpData)
  const authoredData =
    recursiveDataFunction(filterData, COMPONENT_TYPES.PRODUCT_DETAILS).at(0) ||
    {}
  const ip = getClienIP(req) ?? ""
  // Call BV API's after getting pdpData
  const reviewStatsData = await getBVReviewStatsData(skuId)
  const fullReviewData = await getBVFullReviewData(skuId)
  const analyticsData = updateAnalyticPageUrl(rawAnalyticsData, fullUrl)
  const gbhDataLayerCustom = {
    ...(datalayerPageName && {
      datalayerPageName,
    }),
    ...(eventPageType && {
      eventPageType,
    }),
    ...(pageType && {
      pageType,
    }),
  }
  if (pageType) {
    const normalizedCacheTag = akamaiCacheTagNormalization(pageType)
    if (normalizedCacheTag) {
      res.setHeader("Cache-Tag", normalizedCacheTag)
    }
  }
  return {
    props: {
      slug,
      pageModelData: tmpData,
      authoredData,
      header,
      footer,
      pdpData,
      errorsData,
      fullUrl,
      config: configData,
      rawAnalyticsData,
      seoProperties,
      isMultiFinishBundle,
      skuId,
      gbhDataLayerCustom,
      globalScript,
      footerScript,
      siteName,
      ip,
      analyticsData,
      reviewStatsData,
      fullReviewData,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default BundlePdp
